import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NextSeo, OrganizationJsonLd } from 'next-seo';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import { BestOfList, BestOfListItem, SiteReview, Translation } from 'src/types';
import { AdminRoutes, AppRoutes } from 'src/lib/routes';
import ApiClient from 'src/lib/api_client';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import ImageWithFallback from 'src/components/ImageWithFallback';
import RevalidateSettings from 'src/lib/revalidate_settings';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import classNames from 'classnames';
import styles from 'src/styles/modules/pages/best-of-list.module.scss';
import { sortBy, take } from 'lodash-es';
import IlluImage from 'public/images/illu/illu_27.png';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import Link from 'next/link';
import BadgeSvg from 'public/images/svg_icons/badge.svg';
import { FiMenu } from 'react-icons/fi';
import ClickMeSvg from 'public/images/svg_icons/click_me.svg';
import StarSvg from 'public/images/svg_icons/star-1.svg';
import { HEADER_HEIGHT } from 'src/lib/constants';
import { getEleOffset, scrollTo } from 'src/helpers';
import { FiX } from 'react-icons/fi';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { Carousel } from 'react-bootstrap';
import { CurveDiv, CurveText } from 'src/components/CurveText';
import AdminBar from 'src/components/AdminBar';
import useCurrentUser from 'src/hooks/useCurrentUser';
import RedirectNotFound from 'src/components/RedirectNotFound';
import Loading from 'src/components/Loading';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

const HEADER_HEIGHT_AND_PREV_SECTION_BOTTOM_HEIGHT = HEADER_HEIGHT + 144;

const MAPPING_SEASONAL_TYPES = {
  mar_may: 'marMay',
  jun_aug: 'junAug',
  sep_nov: 'sepNov',
  dec_feb: 'decFeb',
};

const MAPPING_TRAVELLER_TYPES = {
  solo: 'solo',
  couples: 'couples',
  families: 'families',
  multi_gen_families: 'multiGenFamilies',
  best_group: 'bestGroup',
};

const ItemMenuPopup: React.FC<{
  items: BestOfListItem[];
  show: boolean;
  currentIndex: number;
  onClose: () => void;
  onClick: (item: BestOfListItem) => void;
}> = ({ items, show, currentIndex, onClose, onClick }) => {
  const popupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (popupRef.current) {
      if (show) {
        disableBodyScroll(popupRef.current);
      } else {
        enableBodyScroll(popupRef.current);
      }
    }
  }, [show]);

  return (
    <>
      <div
        className={classNames('lg:tw-hidden tw-fixed tw-top-0 tw-w-screen tw-h-full tw-z-1200', {
          'tw-hidden': !show,
        })}
      >
        <div
          ref={popupRef}
          className="tw-bg-brand-navy md:tw-px-4 tw-px-5 tw-flex tw-overflow-y-auto custom-scrollbar tw-justify-between tw-pb-8 tw-flex-col tw-h-full"
        >
          <div>
            <div className="tw-pt-4 tw-relative">
              <div
                className="tw-fixed tw-top-6 tw-right-4 tw-cursor-pointer"
                onClick={() => {
                  onClose();
                }}
              >
                <FiX size={25} className="tw-text-brand-green" />
              </div>
              {sortBy(items, 'position').map((item, index) => (
                <div
                  key={item.id}
                  className="tw-flex tw-text-xl tw-items-center tw-font-styleSans tw-font-semibold tw-text-brand-green tw-mb-12 tw-cursor-pointer"
                  style={{ width: '246px' }}
                  onClick={() => {
                    onClick(item);
                  }}
                >
                  <div
                    className={classNames('tw-leading-none md:tw-leading-none tw-mr-4', {
                      'tw-opacity-100 tw-text-xl': index === currentIndex,
                      'tw-opacity-50 tw-text-sm': index !== currentIndex,
                    })}
                  >
                    {(index + 1).toString().padStart(2, '0')}
                  </div>
                  <div
                    className={classNames('tw-leading-none md:tw-leading-none', {
                      'tw-opacity-100 tw-text-3xl': index === currentIndex,
                      'tw-opacity-50 tw-text-xl': index !== currentIndex,
                    })}
                  >
                    {item.title}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const FlipDescriptionCard: React.FC<{
  item: BestOfListItem;
  contentClass?: string;
  bgColorContent: string;
  colorStar: string;
  url: string;
}> = ({ item, contentClass, bgColorContent, colorStar, url }) => {
  const translation = useTranslations('pages/best-of-seek-sophie/[id]')
  const [flip, setFlip] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const bgColorClass = flip ? 'tw-bg-brand-green' : bgColorContent;
  const highlights = item.highlights.filter((o) => o.trim());

  useLayoutEffect(() => {
    const updateFlipCardHeight = () => {
      if (!flip) {
        const initialHeight = textRef.current?.offsetHeight || 0;
        if (initialHeight > 0 && containerRef.current) {
          containerRef.current.style.minHeight = `${initialHeight}px`;
        }
      }
    };
    window.addEventListener('resize', updateFlipCardHeight);
    return () => window.removeEventListener('resize', updateFlipCardHeight);
  }, [flip]);

  return (
    <div
      ref={containerRef}
      className={`${styles.cardDescription} ${contentClass} tw-bg-transparent tw-flex tw-relative tw-z-10`}
    >
      <div
        className={`${bgColorClass} tw-cursor-pointer tw-relative tw-z-20 tw-flex-1 lg:tw-border-t-2 lg:tw-border-l-2 tw-border-b-2 lg:tw-rounded-bl-4xl tw-border-solid tw-border-brand-navy lg:tw-rounded-tl-4xl`}
      >
        <div
          className={classNames(
            'tw-h-full tw-w-full tw-flex tw-flex-col tw-justify-center md:tw-py-24 tw-py-16 tw-cursor-pointer',
            { 'tw-hidden': !flip }
          )}
          onClick={() => setFlip(false)}
        >
          <div className="md:tw-px-11 tw-px-8 md:tw--mr-18 tw--mr-16">
            <div
              className="tw-text-lg"
              dangerouslySetInnerHTML={{ __html: item.descriptionHtml || '' }}
            ></div>
            {item.tip ? (
              <div>
                <div className="tw-border-t tw-border-brand-navy tw-my-8"></div>
                <div className="tw-flex tw-items-center">
                  <div className="tw-mr-4">
                    <div className="tw-relative tw-h-20 tw-w-20">
                      <BadgeSvg className="tw-align-top tw-h-auto tw-w-full" />
                      <div className="tw-text-brand-green tw-text-sm tw-font-semibold tw-uppercase tw-w-24 tw-absolute tw-transform tw-top-1/2 tw--translate-y-1/2 tw-left-1/2 tw--translate-x-1/2 tw-text-center tw-leading-tight md:tw-leading-tight">
                        {translation('top')}
                        <br /> {translation('tip')}
                      </div>
                    </div>
                  </div>
                  <div className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none">
                    {item.tip}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div ref={textRef} className={classNames({ 'tw-hidden': flip })}>
          <div onClick={() => setFlip(true)}>
            <div
              className={`${styles.reveal} reveal-on-scroll tw-font-styleSans tw-font-semibold md:tw-text-4xl tw-text-2xl tw-leading-none md:tw-leading-none xl:tw-pl-12 tw-pl-7 tw-pt-7 xl:tw-pt-15 tw-pr-4 xl:tw-pb-16 tw-pb-8`}
            >
              {item.shortDescription}
            </div>

            <div className="xl:tw--mr-28 tw--mr-24">
              <div className="xl:tw-px-12 tw-px-7 tw-pb-7 xl:tw-pb-12">
                {highlights.length > 0 ? (
                  <>
                    <div
                      className={`${styles.reveal} reveal-on-scroll tw-text-sm tw-font-semibold tw-mb-4`}
                    >
                      {translation('coolThingsHere')}
                    </div>
                    {highlights.map((highlight, idx) => (
                      <div
                        key={idx}
                        className={classNames(
                          `${styles.reveal} reveal-on-scroll tw-font-styleSans tw-font-semibold md:tw-text-2xl tw-text-xl tw-leading-none md:tw-leading-none tw-flex`,
                          { 'tw-mb-4': idx + 1 < item.highlights.length }
                        )}
                      >
                        <StarSvg
                          className={`${colorStar} tw-align-top tw-mr-2 tw-max-w-sm tw-h-4 md:tw-mt-1`}
                        />
                        <div>{highlight}</div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="tw-h-32 md:tw-block tw-hidden"></div>
                )}

                {item.badge ? (
                  <div
                    className={`${styles.reveal} reveal-on-scroll tw-flex tw-justify-end tw-py-4`}
                  >
                    <div className="tw-relative tw-block md:tw-hidden tw-h-32 tw-w-32">
                      <BadgeSvg className="tw-align-top tw-h-full tw-w-full" />
                      <div className="tw-absolute tw-top-0 tw-left-0 tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center tw-px-4">
                        <div className="tw-text-brand-green tw-text-sm tw-font-semibold tw-uppercase tw-text-center tw-leading-tight md:tw-leading-tight">
                          {item.badge}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="tw-h-32 md:tw-hidden tw-block"></div>
                )}
              </div>
            </div>
          </div>
          <div className="md:tw--mr-28 tw--mr-24">
            <Link legacyBehavior href={url}>
              <a
                className={`${styles.bookNowBtn} md:tw-h-18 tw-h-14 tw-w-full tw-text-brand-navy tw tw-bg-brand-green tw-border-t-2 tw-border-solid lg:tw-border-r-2 tw-border-t-brand-navy lg:tw-rounded-b-4xl tw-flex tw-justify-center tw-items-center tw-bg-transparent`}
              >
                <div className="tw-font-styleSans tw-font-semibold tw-leading-none lg:tw-text-base tw-text-sm lg:tw-leading-none tw-text-brand-navy">
                  {translation('bookNow')}
                  <HiOutlineArrowNarrowRight
                    strokeWidth={2}
                    className="tw-text-brand-navy tw-ml-2 md:tw-h-5 md:tw-w-5 tw-w-4 tw-h-4"
                  />
                </div>
              </a>
            </Link>
          </div>
        </div>
        <div
          className={classNames('tw-absolute tw-transform', {
            'lg:tw-w-40 lg:tw-h-16 md:tw--right-28 md:tw-top-11 tw-w-28 tw-h-14 tw--right-20 tw-top-10':
              !flip,
            'lg:tw-w-24 lg:tw-h-14 md:tw--right-16 md:tw-top-6 tw-w-20 tw-h-12 tw--right-14 tw-top-6':
              flip,
          })}
          onClick={() => setFlip(prev => !prev)}
          style={{ rotate: '45deg' }}
        ></div>
      </div>
      <div
        className={classNames('tw-flex tw-flex-col tw-relative tw-z-10', {
          'lg:tw-w-28 tw-w-24': !flip,
          'lg:tw-w-18 tw-w-16': flip,
        })}
      >
        <div
          className={classNames('tw-z-20 tw-relative', {
            'lg:tw-w-28 lg:tw-h-28 tw-w-24 tw-h-24': !flip,
            'lg:tw-w-18 lg:tw-h-18 tw-w-16 tw-h-16': flip,
          })}
        >
          <ClickMeSvg
            className={classNames('tw-w-full tw-h-full', {
              'tw-text-brand-green': !flip,
              'tw-text-brand-yellow': flip,
            })}
          />
          <div
            onClick={() => {
              setFlip(prev => !prev);
            }}
            className={classNames('tw-absolute lg:tw-w-auto tw-w-10', {
              'lg:tw-top-16 lg:tw-left-0.5 tw-top-11 tw-left-2 tw-transform tw-rotate-45': !flip,
              'lg:tw-top-9 lg:tw-left-3 tw-top-8 tw-left-2': flip,
            })}
          >
            {flip ? (
              <FiX size={20} className="tw-text-brand-navy" />
            ) : (
              <div className="tw-text-center tw-font-semibold tw-uppercase md:tw-text-sm tw-text-xs md:tw-w-20 tw-w-18">
                {translation('clickMe')}
              </div>
            )}
          </div>
        </div>
        <div
          onClick={() => setFlip(prev => !prev)}
          className={`${bgColorClass} tw-h-full tw-cursor-pointer lg:tw-border-r-2 tw-border-b-2 lg:tw-rounded-br-4xl tw-border-solid tw-border-brand-navy tw-relative`}
        >
          <div
            className={`${bgColorClass} tw-absolute lg:tw--top-10 tw--top-7 tw-w-1/2 lg:tw-h-10 tw-h-7`}
          ></div>
        </div>
      </div>
    </div>
  );
};

const BestOfListItemCard: React.FC<{
  index: number;
  item: BestOfListItem;
}> = ({ index, item }) => {
  const number = (index + 1).toString().padStart(2, '0');
  const hasImageLeft = index % 2 === 0;
  const resolveRoute = (linkableSlug: string | null, linkableType: string | null) => {
    if (!linkableSlug || !linkableType) return '#';
    return linkableType === 'City'
      ? AppRoutes.cityRoute({ id: linkableSlug }).toPath()
      : linkableType === 'Collection'
      ? AppRoutes.collectionRoute({ id: linkableSlug }).toPath()
      : AppRoutes.experienceRoute({ id: linkableSlug }).toPath();
  };

  return (
    <div
      className={classNames(
        'tw-justify-center lg:tw-px-10 lg:tw-py-18 tw-flex tw-flex-col lg:tw-flex-row'
      )}
    >
      <div
        className={classNames(`${styles.cardImage} tw-relative`, {
          'lg:tw-order-1 lg:tw--mr-24': hasImageLeft,
          'lg:tw-order-2 lg:tw--ml-24': !hasImageLeft,
        })}
      >
        <div className="tw-h-full tw-w-full">
          <Carousel
            controls={item.images.length > 1}
            prevIcon={null}
            nextIcon={null}
            indicators={item.images.length > 1}
            className="carousel-best-of-seeksophie-image tw-mb-3 carousel slide"
            interval={null}
            pause={false}
            keyboard={false}
            touch={true}
          >
            {take(item.images, 8).map((image) => (
              <Carousel.Item key={image.id} className="carousel-item tw-relative">
                <ImageWithFallback
                  record={image}
                  imgField="webpXlUrl"
                  fill
                  priority={true}
                  alt={item.title || ''}
                  className="tw-static tw-object-cover lg:tw-border-2 lg:tw-border-brand-green lg:tw-border-solid lg:tw-rounded-lg"
                />
              </Carousel.Item>
            ))}
          </Carousel>

          {item.badge ? (
            <div
              className={classNames('lg:tw-absolute lg:tw-block tw-hidden md:tw--top-6', {
                'md:tw--left-11': hasImageLeft,
                'md:tw--right-11': !hasImageLeft,
              })}
            >
              <div className="tw-relative">
                <BadgeSvg className="tw-align-top tw-h-auto tw-w-32" />
                <div className="tw-absolute tw-top-0 tw-left-0 tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center tw-px-4">
                  <div className="tw-text-brand-green tw-text-sm tw-font-semibold tw-uppercase tw-text-center tw-leading-tight md:tw-leading-tight">
                    {item.badge}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <div className="lg:tw-hidden tw-absolute tw-left-8 tw-top-10">
            <div
              className={classNames(
                'tw-flex tw-justify-center tw-items-center tw-rounded-full tw-border-grey-lighter tw-border-2 tw-border-brand-navy tw-border-solid tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none tw-text-3xl tw-mb-6 tw-max-w-max tw-px-3 tw-py-2',
                {
                  'tw-bg-purple': hasImageLeft,
                  'tw-bg-orange-light': !hasImageLeft,
                }
              )}
            >
              <div className="tw-mt-1">{number}</div>
            </div>

            <Link legacyBehavior href={resolveRoute(item.linkableSlug, item.linkableType)}>
              <a className="tw-text-brand-navy">
                <div
                  className={`${styles.titleUnderline} tw-max-w-xs tw-text-5xl tw-text-white tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none`}
                >
                  {item.title}
                </div>
              </a>
            </Link>
          </div>
        </div>
      </div>
      <div
        className={classNames(`${styles.cardDescription} lg:tw-relative lg:tw--mt-0 tw--mt-24`, {
          'lg:tw-order-1 lg:tw-block': !hasImageLeft,
          'lg:tw-order-2 lg:tw-block': hasImageLeft,
        })}
      >
        <div className={classNames('tw-hidden lg:tw-block tw-mb-12', { 'tw-ml-32': hasImageLeft })}>
          <div
            className={classNames(
              'tw-flex tw-justify-center tw-items-center tw-rounded-full tw-border-grey-lighter tw-border-2 tw-border-brand-navy tw-border-solid tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none tw-text-4xl tw-mb-4 tw-max-w-max tw-py-4 tw-px-6',
              {
                'tw-bg-purple': hasImageLeft,
                'tw-bg-orange-light': !hasImageLeft,
              }
            )}
          >
            <div className="tw-mt-1">{number}</div>
          </div>
          <h2
            className={classNames(
              `${styles.titleUnderline} tw-text-6xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none`,
              {
                'md:tw-max-w-xs': !hasImageLeft,
              }
            )}
          >
            <Link legacyBehavior href={resolveRoute(item.linkableSlug, item.linkableType)}>
              <a className="tw-text-brand-navy">{item.title}</a>
            </Link>
          </h2>
        </div>

        <FlipDescriptionCard
          item={item}
          url={resolveRoute(item.linkableSlug, item.linkableType)}
          bgColorContent={hasImageLeft ? 'tw-bg-white' : 'tw-bg-brand-yellow'}
          colorStar={hasImageLeft ? 'tw-text-purple' : 'tw-text-orange-light'}
        />
      </div>
    </div>
  );
};

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  bestOfList,
  siteReview,
}) => {
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/best-of-seek-sophie/[id]')
  const [currentUser, , , , isLoadingUser] = useCurrentUser();
  const [show, setShow] = useState(false);
  const bestOfListItemsRef = useRef<HTMLDivElement | null>(null);
  const [currentItemId, setCurrentItemId] = useState<number | undefined>(12);
  const increaseViewCount = useEnhanceApiClient(ApiClient.increaseViewCount);
  const items = sortBy(bestOfList.items, ['position']);
  const currentIndex = items.findIndex((o) => o.id === currentItemId);
  const containerItemsRef = useRef<HTMLDivElement | null>(null);
  const divCurveRef = useRef<HTMLDivElement>(null);
  const curveRef = useRef<HTMLDivElement | null>(null);
  const [curveTextRadius, setCurveTextRadius] = useState<number>(3000);
  const [curveDivRadius, setCurveDivRadius] = useState<number>(0);
  const { isScreenMd } = useBreakpoints();

  useEffect(() => {
    const applyParallaxEffect = () => {
      const heroImage = document.querySelector('#hero-image') as HTMLDivElement;
      if (!heroImage) return;
      const scrollPosition = window.pageYOffset;
      heroImage.style.transform = `translateY(${scrollPosition * 0.5}px)`;
    };

    const updateCurveTextRadius = () => {
      if (!curveRef.current) return;
      const minPx = 0;
      const maxPx = getEleOffset(curveRef.current).top;
      const minVal = 20;
      const maxVal = 0;
      const minPaddingVal = 16;
      const maxPaddingVal = isScreenMd ? 120 : 0;
      const radius = Math.max(
        ((window.scrollY - minPx) / (maxPx - minPx)) * (maxVal - minVal) + minVal,
        2
      );
      const padding = Math.min(
        ((window.scrollY - minPx) / (maxPx - minPx)) * (maxPaddingVal - minPaddingVal) +
          minPaddingVal,
        maxPaddingVal
      );

      setCurveTextRadius(Number(radius.toFixed(3)));
      curveRef.current.style.paddingBottom = `${padding.toFixed(0)}px`;
    };

    const updateCurveDivRadius = () => {
      if (!divCurveRef.current) return;
      const divCurveEle = divCurveRef.current;
      const minPx = 0;
      const maxPx = getEleOffset(divCurveEle).top;
      const minVal = 0;
      const maxVal = 4;
      const radius = Math.min(
        ((window.scrollY - minPx) / (maxPx - minPx)) * (maxVal - minVal) + minVal,
        2
      );
      setCurveDivRadius(Number(radius.toFixed(3)));
    };

    const revealText = () => {
      items.map((o) => {
        const item = document.querySelector(`#item${o.id}`) as HTMLDivElement;
        if (item) {
          const groupTop = getEleOffset(item).top;
          const groupBottom = groupTop + item.offsetHeight;
          const scrollY = window.scrollY + window.innerHeight / 2;
          const texts = item.querySelectorAll('.reveal-on-scroll');

          if (scrollY > groupTop && scrollY < groupBottom) {
            texts.forEach((el, i) => {
              const element = el as HTMLDivElement;
              element.classList.add(styles.revealInView);
              element.style.setProperty('--delay', `${i * 0.2}s`);
            });
          } else if (scrollY < groupTop) {
            texts.forEach((el) => {
              el.classList.remove(styles.revealInView);
            });
          }
        }
      });
    };

    const findActiveItem = () => {
      if (!bestOfListItemsRef.current || !containerItemsRef.current) return;
      const bestOfListItemsEle = bestOfListItemsRef.current;
      const scrollPos = window.scrollY;
      let nextCurrentIndex: number | undefined;
      let height = 0;

      items.map((item, index) => {
        const sectionEle: HTMLElement | null = document.querySelector(`#item${item.id}`);
        if (!sectionEle) return;

        var positionTop =
          getEleOffset(sectionEle).top - HEADER_HEIGHT_AND_PREV_SECTION_BOTTOM_HEIGHT;
        const isScrollPosPass = positionTop <= scrollPos;
        if (isScrollPosPass && positionTop + sectionEle.offsetHeight > scrollPos) {
          setCurrentItemId(item.id);
          nextCurrentIndex = index;
          const links = Array.from(bestOfListItemsEle.children) as HTMLElement[];
          height = [...Array(index)]
            .map((_, i) => i)
            .reduce((sum, i) => {
              sum =
                sum +
                (index + 1 === items.length
                  ? bestOfListItemsEle.scrollHeight
                  : links[i].offsetHeight);
              return sum;
            }, 0);
        }
      });
      if (
        typeof nextCurrentIndex === 'number' &&
        bestOfListItemsEle &&
        bestOfListItemsEle.scrollTop !== height
      ) {
        bestOfListItemsEle.scrollTo({
          left: 0,
          top: height,
          behavior: 'smooth',
        });
      }
    };

    document.addEventListener('scroll', findActiveItem);
    document.addEventListener('scroll', updateCurveTextRadius);
    document.addEventListener('scroll', applyParallaxEffect);
    document.addEventListener('scroll', updateCurveDivRadius);
    document.addEventListener('scroll', revealText);
    return () => {
      document.removeEventListener('scroll', findActiveItem);
      document.removeEventListener('scroll', updateCurveTextRadius);
      document.removeEventListener('scroll', applyParallaxEffect);
      document.removeEventListener('scroll', updateCurveDivRadius);
      document.removeEventListener('scroll', revealText);
    };
  }, []);

  useEffect(() => {
    if (bestOfList.id) {
      increaseViewCount({
        view_countable_id: bestOfList.id,
        view_countable_type: 'BestOfList',
      });
    }
  }, [bestOfList.id]);

  const isUnpublished = bestOfList.status !== 'published';
  const bestOfListTitle = (() => {
    if (bestOfList.kind === 'general') {
      return translation('titleGeneral', {title: bestOfList.title})
    }

    if (bestOfList.kind === 'seasonal' && bestOfList.seasonalType) {
      return translation('titleSeasonal', {
        title: translation(MAPPING_SEASONAL_TYPES[bestOfList.seasonalType])
      })
    }

    if (bestOfList.kind === 'traveller' && bestOfList.travellerType) {
      return translation('titleTraveller', {
        title: translation(MAPPING_TRAVELLER_TYPES[bestOfList.travellerType])
      })
    }

    return ''
  })()

  return (
    <>
      <NextSeo
        title={bestOfList.title || ''}
        description={bestOfList.description || ''}
        canonical={addLocaleToUrl(AppRoutes.bestOfListRoute({ id: bestOfList.slugCode }).toUrl())}
        noindex={isUnpublished ? true : undefined}
        nofollow={isUnpublished ? true : undefined}
        openGraph={{
          url: addLocaleToUrl(AppRoutes.bestOfListRoute({ id: bestOfList.slugCode }).toUrl()),
          title: bestOfList.title || '',
          description: bestOfList.description || '',
          images: bestOfList.image
            ? [
                {
                  url: bestOfList.image.mdUrl,
                  height: 600,
                  width: 600,
                  alt: bestOfList.title || '',
                },
              ]
            : undefined,
        }}
      />
      <WithDefaultJsonLd>
        <OrganizationJsonLd
          name={bestOfList.title || ''}
          url={AppRoutes.bestOfListRoute({
            id: bestOfList.slug || bestOfList.id,
          }).toUrl()}
          image={bestOfList.image?.mdUrl || ''}
          description={bestOfList.description}
          aggregateRating={{
            ratingValue: siteReview.rating,
            reviewCount: siteReview.count,
          }}
        />
      </WithDefaultJsonLd>

      {isUnpublished && isLoadingUser ? (
        <Loading className="tw-justify-center tw-py-20" />
      ) : isUnpublished && !currentUser?.isAdmin ? (
        <RedirectNotFound />
      ) : (
        <>
          <AdminBar updatedAt={bestOfList.updatedAt}>
            <>
              <a
                href={AdminRoutes.adminEditBestOfListRoute({
                  id: bestOfList.id || bestOfList.slug,
                }).toUrl()}
                className="btn btn-primary mb-2"
              >
                Edit List
              </a>
              <a
                href={AdminRoutes.adminBestOfListsRoute({}).toUrl()}
                className="btn btn-outline-primary mb-2"
              >
                Best Of Dashboard
              </a>
            </>
          </AdminBar>

          <div className={`${styles.heroImgContainer}`}>
            <div
              id="hero-image"
              className={styles.heroImgParallaxImage}
              style={{
                backgroundImage: `url(${bestOfList.image?.webpXlUrl})`,
              }}
            ></div>

            <div
              className="tw-w-full tw-h-full tw-absolute tw-top-0 tw-left-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)',
              }}
            ></div>

            <div className="tw-left-0 tw-absolute tw-z-10 tw-top-0 tw-w-full tw-h-full md:tw-pt-24 tw-pt-16">
              <div className="container">
                <div className="tw-flex tw-items-start tw-justify-between tw-relative">
                  <div className="tw-font-styleSans tw-font-semibold tw-text-brand-green md:tw-text-6xl tw-text-3xl tw-leading-none md:tw-leading-none">
                    SEEK
                  </div>
                  <h1 className="tw-absolute md:tw-relative tw-top-15 md:tw-top-0 tw-left-1/2 md:tw-left-auto tw-transform md:tw-transform-none tw--translate-x-1/2 tw-w-full tw-font-styleSans tw-font-semibold tw-text-brand-green md:tw-text-9xl tw-text-6xl tw-leading-none md:tw-leading-none tw-text-center tw-max-w-xs md:tw-max-w-xl tw-mx-auto">
                    {bestOfListTitle}
                  </h1>
                  <div className="tw-font-styleSans tw-font-semibold tw-text-brand-green md:tw-text-6xl tw-text-3xl tw-leading-none md:tw-leading-none">
                    SOPHIE
                  </div>
                </div>
              </div>
              <div className="tw-absolute tw-bottom-0 tw-w-full tw-text-center md:tw-pb-24 tw-pb-16">
                <div className="container">
                  <div className="tw-mb-4 tw-text-brand-green" ref={curveRef}>
                    <CurveText
                      radius={curveTextRadius}
                      id="best_lists"
                      text={[
                        items
                          .slice(0, isScreenMd ? 6 : 4)
                          .map((o) => o.title)
                          .join(' / '),
                        items.length > (isScreenMd ? 6 : 4) ? translation('andMore') : null,
                      ].join(' ')}
                      className="tw-max-w-4xl tw-mx-auto md:tw-text-2xl tw-text-xl tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-uppercase"
                    />

                    <div
                      className={classNames('tw-transform tw--rotate-90 tw-cursor-pointer', {
                        'tw-invisible': curveDivRadius > 0,
                      })}
                      onClick={() =>
                        containerItemsRef.current
                          ? scrollTo(containerItemsRef.current, -HEADER_HEIGHT)
                          : null
                      }
                    >
                      <HiOutlineArrowNarrowLeft
                        strokeWidth={2}
                        className="tw-text-brand-green tw-w-5 tw-h-6 md:tw-w-6 md:tw-h-7"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div ref={divCurveRef} className="tw-absolute tw-bottom-0 tw-w-full tw-left-0">
                <CurveDiv
                  scale={curveDivRadius}
                  className="tw-bg-brand-green tw-text-brand-green"
                />
              </div>
            </div>
          </div>
          <div className="tw-bg-brand-green md:tw-py-36 tw-py-32">
            <div className="container tw-text-center tw-max-w-3xl">
              <div className="md:tw-text-6xl tw-text-4xl tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none md:tw-mb-14 tw-mb-6">
                {translation('editNote')}
              </div>
              <div className="tw-font-styleSans tw-font-semibold md:tw-text-2xl tw-text-xl tw-leading-none md:tw-leading-none">
                {bestOfList.description}
              </div>
            </div>
          </div>

          <div className="tw-bg-brand-yellow">
            <div className="tw-flex">
              <div className="tw-hidden lg:tw-block tw-bg-brand-navy">
                <div
                  ref={bestOfListItemsRef}
                  className={`${styles.menuSideBar} tw-sticky tw-top-18 tw-py-15 tw-overflow-x-hidden tw-overflow-y-auto hide-scrollbar tw-h-screen tw-px-4`}
                >
                  {items.map((item, index) => (
                    <div
                      key={item.id}
                      className="tw-flex tw-items-center tw-font-styleSans tw-font-semibold tw-text-brand-green tw-mb-16 tw-cursor-pointer"
                      onClick={() => {
                        const ele = document.querySelector(`#item${item.id}`);
                        if (ele instanceof HTMLElement && containerItemsRef.current) {
                          scrollTo(ele, -HEADER_HEIGHT);
                        }
                      }}
                    >
                      <div
                        className={classNames('tw-leading-none md:tw-leading-none tw-mr-4', {
                          'tw-opacity-100 tw-text-2xl': index === currentIndex,
                          'tw-opacity-50 tw-text-sm': index !== currentIndex,
                        })}
                      >
                        {(index + 1).toString().padStart(2, '0')}{' '}
                      </div>
                      <div
                        className={classNames('tw-leading-none md:tw-leading-none', {
                          'tw-opacity-100 tw-text-4xl': index === currentIndex,
                          'tw-opacity-50 tw-text-2xl': index !== currentIndex,
                        })}
                      >
                        {item.title}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="tw-w-full tw-px-0 tw-relative">
                <div className="lg:tw-hidden tw-sticky tw-top-14 tw-z-90">
                  <div
                    className="tw-bg-brand-green tw-absolute tw-top-6 md:tw-right-7 tw-right-3.75 tw-rounded-full tw-max-w-max tw-flex tw-justify-center tw-items-center tw-p-2.5"
                    onClick={() => setShow(true)}
                  >
                    <FiMenu size={16} className="tw-text-brand-navy" />
                  </div>
                </div>
                <div ref={containerItemsRef}></div>
                {items.map((item, index) => (
                  <div
                    key={item.id}
                    id={`item${item.id}`}
                    className={classNames({
                      'tw-bg-brand-yellow': index % 2 === 0,
                      'tw-bg-white': index % 2 !== 0,
                    })}
                  >
                    <BestOfListItemCard item={item} index={index} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="tw-bg-purple tw-py-16 md:tw-py-24">
            <div className="container tw-text-center">
              <div className="md:tw-text-lg tw-text-sm tw-font-semibold">
                {translation('needMoreInspo')}
              </div>
              <div className="md:tw-py-12 tw-py-8">
                <ImageWithFallback
                  src={IlluImage}
                  alt=""
                  className="tw-inline-block md:tw-h-96 tw-h-64 tw-w-auto"
                />
              </div>
              <Link legacyBehavior href={AppRoutes.bestOfListsRoute().toPath()}>
                <a className="tw-text-brand-navy tw-font-styleSans tw-font-semibold md:tw-text-6xl tw-text-5xl tw-underline tw-leading-none md:tw-leading-none">
                  <div className="tw-mb-4">
                    <HiOutlineArrowNarrowLeft
                      size={30}
                      strokeWidth={2}
                      className="tw-text-brand-navy"
                    />
                  </div>
                  <div>{translation('backToAllLists')}</div>
                </a>
              </Link>
            </div>
          </div>
          <ItemMenuPopup
            show={show}
            items={items}
            currentIndex={currentIndex}
            onClose={() => setShow(false)}
            onClick={(item) => {
              const ele = document.querySelector(`#item${item.id}`);
              const MOBILE_HEADER_HEIGHT = 54;
              if (ele instanceof HTMLElement && containerItemsRef.current) {
                scrollTo(ele, -MOBILE_HEADER_HEIGHT);
              }
              setShow(false);
            }}
          />
        </>
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{
  bestOfList: BestOfList;
  siteReview: SiteReview;
}> = async (context) => {
  if (!context.params?.id) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.medium,
    };
  }

  const translations = await prepareTranslations(context, ['pages/best-of-seek-sophie/[id]']);
  const [bestOfList] = await ApiClient.getBestOfList(
    { id: String(context.params.id) },
    { 'X-NEXT-LOCALE': translations.locale }
  );

  if ('error' in bestOfList) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.medium,
    };
  }

  const [siteReview] = await ApiClient.getSiteReviews();

  return {
    props: {
      bestOfList,
      siteReview,
      ...translations
    },
    revalidate: RevalidateSettings.medium,
  };
};

export default Page;
